/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import FancyScroll from "@eisinc/App/Components/FancyScroll/FancyScroll";
import SalesRepComponent from "@eisinc/App/Components/SalesRep/SalesRep";
import styles from "@eisinc/App/Widgets/MyAccountQuickLinks/MyAccountQuickLinks.style";
import ChevronRight from "@eisinc/XDL/Icons/ChevronRight";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLink, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import React from "react";
import { connect } from "react-redux";

export interface MyAccountQuickLinkModel {
    fields: {
        imageUrl: string;
        title: string;
        linkText: string;
        link: LinkFieldValue;
    };
    link: any;
}

const enum fields {
    links = "links",
}

interface MyAccountQuickLinksProps extends WidgetProps {
    [fields.links]: MyAccountQuickLinkModel[];
}

const mapStateToProps = (state: ApplicationState, myAccountQuickLinksProps: MyAccountQuickLinksProps) => {
    if (myAccountQuickLinksProps.fields.links) {
        return {
            processedLinks: myAccountQuickLinksProps.fields[fields.links]
                .map((link: MyAccountQuickLinkModel) => {
                    return {
                        ...link,
                        link: getLink(state, link.fields.link),
                    };
                })
                .filter((link: any) => {
                    return !link?.link?.excludeFromNavigation;
                }),
            session: getSession(state),
        };
    }
    return {
        processedLinks: [],
        session: getSession(state),
    };
};

type Props = MyAccountQuickLinksProps & ReturnType<typeof mapStateToProps>;

const MyAccountQuickLinksTemplateWidget: React.FC<Props> = (props: Props) => {
    const secondarySalesRep =
        props.session.properties["secondarySalesRep"] && JSON.parse(props.session.properties["secondarySalesRep"]);
    const primarySalesRep =
        props.session.properties["primarySalesRep"] && JSON.parse(props.session.properties["primarySalesRep"]);
    const { session } = props;

    return (
        <StyledWrapper css={styles.widgetContainer}>
            <StyledWrapper css={styles.salesRepContainer}>
                <FancyScroll showOverlay={true}>
                    <StyledWrapper css={styles.salesRepContainerHeader}>
                        <Typography css={styles.welcomeStyles}>
                            {`${translate("Welcome ") + (session.firstName || session.userName)}!`}
                        </Typography>
                        <Typography css={styles.salesRepSubTitleStyles}>
                            {translate("Get in touch with your EIS rep(s):")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper css={styles.salesRepBodyContainer}>
                        {!primarySalesRep && !secondarySalesRep ? (
                            siteMessage("Custom_No_Sales_Rep", "No sales rep for this account")
                        ) : (
                            <SalesRepComponent smallImageSize={true} />
                        )}
                    </StyledWrapper>
                </FancyScroll>
            </StyledWrapper>
            <GridContainer
                css={styles.linksContainer}
                offsetProps={{
                    style: { justifyContent: "space-around" },
                }}
            >
                {props.processedLinks &&
                    props.processedLinks.map((link: MyAccountQuickLinkModel) => {
                        return (
                            <GridItem width={[12, 6, 6, 3, 3]} key={link.fields.link.value} css={styles.linkGridItem}>
                                <Link href={link?.link?.url} aria-label={link.fields.linkText} css={styles.link}>
                                    <StyledWrapper css={...styles.linkContentContainer}>
                                        <StyledWrapper css={...styles.imageWrapper}>
                                            <styles.styledImage src={link.fields.imageUrl} alt={link.fields.linkText} />
                                        </StyledWrapper>
                                        <StyledWrapper css={styles.titleTextStyles}>
                                            <Typography css={styles.titleStyles}>{link.fields.title}</Typography>
                                            {link.fields.linkText && (
                                                <StyledWrapper css={styles.linkTextContainer}>
                                                    <Typography css={styles.linkTextStyles}>
                                                        {link.fields.linkText}
                                                    </Typography>
                                                    <Icon src={ChevronRight} css={styles.chevronRightIconStyle} size={12} />
                                                </StyledWrapper>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </Link>
                            </GridItem>
                        );
                    })}
            </GridContainer>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                const { type, value } = item.fields.link;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return link?.title;
                }
                if (type === "Category") {
                    return value; // TODO ISC-10781 make this work
                }
                if (type === "Url") {
                    return value;
                }
                return value;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "link",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                },
                {
                    name: "imageUrl",
                    displayName: "Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "title",
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "linkText",
                    displayName: "Link Text",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountQuickLinksTemplateWidget),
    definition,
};

export default widgetModule;
